import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Autocomplete, CircularProgress, Divider, Grid, InputAdornment, useMediaQuery, useTheme, Chip,  } from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const NewOrder = () => {
  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(1); // 1: انتخاب مشتری، 2: توضیحات
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // مشتری
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  
  // فرم مشتری جدید
  const [newCustomer, setNewCustomer] = useState({
    full_name: '',
    mobile: '',
    address: '',
  });
  
  // توضیحات سفارش
  const [description, setDescription] = useState('');
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // جستجوی مشتری
  useEffect(() => {
    if (customerSearch.length >= 2) {
      searchCustomers();
    } else {
      setCustomers([]);
    }
  }, [customerSearch]);

  const searchCustomers = async () => {
    setSearchLoading(true);
    try {
      const response = await client.get('/orders/customers/', {
        params: { search: customerSearch }
      });
      setCustomers(response.data.results || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    navigate('/shop');
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsNewCustomer(false);
    setStep(2);
  };

  const handleNewCustomer = () => {
    setIsNewCustomer(true);
    setSelectedCustomer(null);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setDescription('');
  };

  const validateMobile = (mobile) => {
    return /^09\d{9}$/.test(mobile);
  };

  const handleSubmit = async () => {
    // اعتبارسنجی
    if (isNewCustomer) {
      if (!newCustomer.full_name.trim()) {
        toast.error('نام مشتری الزامی است');
        return;
      }
      if (!validateMobile(newCustomer.mobile)) {
        toast.error('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
        return;
      }
    }
    
    if (!description.trim()) {
      toast.error('توضیحات سفارش الزامی است');
      return;
    }

    setLoading(true);
    try {
      const data = {
        description: description.trim(),
      };

      if (isNewCustomer) {
        data.customer_name = newCustomer.full_name;
        data.customer_mobile = newCustomer.mobile;
        data.customer_address = newCustomer.address;
      } else {
        data.customer_id = selectedCustomer.id;
      }

      const response = await client.post('/orders/create/', data);
      const orderData = response.data?.order;
      const orderNumber = orderData?.order_number || '';
      toast.success(`سفارش ${orderNumber} با موفقیت ثبت شد`);
      navigate('/shop/orders');
    } catch (error) {
      toast.error(error.response?.data?.error || 'خطا در ثبت سفارش');
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100%' : 'auto',
        }
      }}
    >
      {/* هدر */}
      <DialogTitle
        sx={{
          bgcolor: themeColors.dark,
          color: themeColors.gold,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          <Typography variant="h6" fontWeight="bold">
            {step === 1 ? 'انتخاب مشتری' : 'ثبت پیش‌سفارش'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: themeColors.gold }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* مرحله 1: انتخاب مشتری */}
        {step === 1 && (
          <Box sx={{ p: 2 }}>
            {/* جستجوی مشتری */}
            <CustomTextField               fullWidth
              placeholder="جستجوی مشتری (نام یا موبایل)..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchLoading && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* لیست مشتریان */}
            {customers.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  نتایج جستجو
                </Typography>
                {customers.map((customer) => (
                  <Card
                    key={customer.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" fontWeight="bold">
                            {customer.full_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" dir="ltr">
                            {toPersianDigits(customer.mobile)}
                          </Typography>
                        </Box>
                        {customer.orders_count > 0 && (
                          <Chip
                            size="small"
                            label={`${toPersianDigits(customer.orders_count.toString())} سفارش`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* دکمه مشتری جدید */}
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">یا</Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleNewCustomer}
              sx={{
                py: 2,
                borderStyle: 'dashed',
                borderWidth: 2,
              }}
            >
              ثبت مشتری جدید
            </Button>
          </Box>
        )}

        {/* مرحله 2: اطلاعات سفارش */}
        {step === 2 && (
          <Box sx={{ p: 2 }}>
            {/* نمایش مشتری انتخاب شده */}
            {!isNewCustomer && selectedCustomer && (
              <Card sx={{ mb: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon />
                    <Box>
                      <Typography variant="body2">مشتری:</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedCustomer.full_name} - {toPersianDigits(selectedCustomer.mobile)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* فرم مشتری جدید */}
            {isNewCustomer && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  اطلاعات مشتری جدید
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomTextField                       fullWidth
                      label="نام و نام خانوادگی *"
                      value={newCustomer.full_name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, full_name: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField                       fullWidth
                      label="شماره موبایل *"
                      value={newCustomer.mobile}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                        setNewCustomer({ ...newCustomer, mobile: val });
                      }}
                      placeholder="09123456789"
                      inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField                       fullWidth
                      label="آدرس"
                      value={newCustomer.address}
                      onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* توضیحات سفارش */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              توضیحات سفارش *
            </Typography>
            <CustomTextField               fullWidth
              multiline
              rows={5}
              placeholder="توضیحات سفارش را وارد کنید...&#10;مثال: پرده پذیرایی، ۲ عدد پنجره، والان دار..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* دکمه‌ها */}
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {step === 2 && (
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ minWidth: 100 }}
          >
            بازگشت
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              flexGrow: 1,
              py: 1.5,
              bgcolor: themeColors.gold,
              color: themeColors.dark,
              '&:hover': { bgcolor: themeColors.goldLight },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'ثبت پیش‌سفارش'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewOrder;